import React, { useState, useEffect } from "react";
import { Group, Text, AppShell, ActionIcon, Drawer, Badge, Avatar, Skeleton, Tooltip, Modal, Button, Stack, Divider, Paper, Box, Transition, Menu, UnstyledButton } from "@mantine/core";
import { IconBell, IconUser, IconInfoCircle, IconMail, IconPhone, IconBuildingSkyscraper, IconBriefcase, IconChevronRight, IconArrowLeft, IconCircleDot, IconSettings, IconLogout, IconSearch, IconCommand } from "@tabler/icons-react";
import NotificationDrawer from "../../../shared/components/ui/NotificationSystem/NotificationDrawer";
import PortalNotificationBell from "../../../components/Notifications/PortalNotificationBell";
import { useUIEnhancements } from "../../../hooks/ui/useUIEnhancements";
import { useSendNotification } from "../../../hooks/notifications/useSendNotification.jsx";
import { useAuth } from "../../../hooks/core/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import brandLogoUrl from "../../../../zippe_no_bg.webp";

const Header = () => {
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [time, setTime] = useState(new Date());
  const { isOnline } = useUIEnhancements();
  const { notifications } = useSendNotification();
  const { user, staffProfile, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [logoOk, setLogoOk] = useState(true);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Add pulse animation styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Get user information with proper fallbacks
  const userName = staffProfile?.displayName || user?.email?.split('@')[0] || "User";
  const userRole = staffProfile?.role || "Staff";
  const userEmail = staffProfile?.email || user?.email || "No email available";
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  
  // Format time
  const formattedTime = time.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });

  const formattedDate = time.toLocaleDateString('en-US', { 
    weekday: 'short',
    month: 'short', 
    day: 'numeric' 
  });

  // Determine role badge color with gradient
  const getRoleGradient = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "linear-gradient(135deg, var(--mantine-color-accent-5), var(--mantine-color-amber-5))";
      case "manager":
        return "linear-gradient(135deg, var(--mantine-color-accent-4), var(--mantine-color-amber-4))";
      case "staff":
        return "linear-gradient(135deg, var(--mantine-color-accent-3), var(--mantine-color-amber-3))";
      default:
        return "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)";
    }
  };

  // Get page title from location
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    const title = path.split('/').filter(Boolean).map(segment => 
      segment.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    ).join(' / ');
    return title;
  };

  return (
    <>
      <AppShell.Header 
        height={77} 
        style={{ 
          background: "linear-gradient(90deg, rgba(17, 24, 39, 0.98) 0%, rgba(31, 41, 55, 0.98) 100%)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          zIndex: 2000,
          paddingTop: 0,
          paddingBottom: 0,
          overflow: 'hidden',
        }}
      >
        <Group justify="space-between" align="center" h="100%" px="md" style={{ position: "relative" }}>
          {/* Left Section - Navigation and Title */}
          <Group gap="md" align="center">
            {location.pathname !== '/' && (
              <Tooltip label="Go back" withArrow>
                <ActionIcon
                  onClick={() => navigate(-1)}
                  style={{
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    transition: "all 0.3s ease",
                  }}
                  radius="md"
                  size="md"
                  variant="subtle"
                >
                  <IconArrowLeft size={18} stroke={1.5} style={{ color: "rgba(255, 255, 255, 0.7)" }} />
                </ActionIcon>
              </Tooltip>
            )}
            
            <Group gap="lg">
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 0,
                  borderRadius: 0,
                  background: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                }}
              >
                {logoOk ? (
                  <img
                    src={brandLogoUrl}
                    alt="Zippe"
                    height={20}
                    style={{ display: 'block', objectFit: 'contain' }}
                    onError={() => setLogoOk(false)}
                  />
                ) : (
                  <Text size="xs" fw={800} style={{ color: 'white', letterSpacing: '0.08em' }}>ZIPPE</Text>
                )}
              </Box>
              <Text 
                size="sm" 
                fw={600} 
                style={{ 
                  color: "rgba(255, 255, 255, 0.95)",
                  letterSpacing: "0.01em"
                }}
              >
                {getPageTitle()}
              </Text>
            </Group>
          </Group>

          {/* Center Section - Search and Time */}
          <Group gap="xl" align="center" style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
            <Tooltip label="Search (⌘K)" withArrow>
              <ActionIcon
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  transition: "all 0.3s ease",
                }}
                radius="md"
                size="md"
                variant="subtle"
              >
                <IconSearch size={16} stroke={1.5} style={{ color: "rgba(255, 255, 255, 0.5)" }} />
              </ActionIcon>
            </Tooltip>
            
            <Group gap="xs">
              <Text size="xs" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                {formattedDate}
              </Text>
              <Text size="sm" fw={600} style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                {formattedTime}
              </Text>
            </Group>
          </Group>

          {/* Right Section - Notifications and Profile */}
          <Group gap="md" align="center">
            {/* Notification Bell */}
            <Box style={{ position: "relative" }}>
              <ActionIcon
                onClick={() => setIsNotificationDrawerOpen(true)}
                style={{
                  background: notifications?.length > 0 
                    ? "linear-gradient(135deg, var(--mantine-color-accent-5), var(--mantine-color-amber-5))"
                    : "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  transition: "all 0.3s ease",
                  position: "relative",
                }}
                radius="md"
                size="md"
                variant="subtle"
              >
                <IconBell 
                  size={18} 
                  stroke={1.5} 
                  style={{ 
                    color: notifications?.length > 0 ? "white" : "rgba(255, 255, 255, 0.7)",
                    animation: notifications?.length > 0 ? "pulse 2s infinite" : "none"
                  }} 
                />
              </ActionIcon>
              {notifications?.length > 0 && (
                <Box
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--mantine-color-amber-5), var(--mantine-color-accent-5))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(251, 191, 36, 0.5)",
                    border: "2px solid rgba(17, 24, 39, 0.98)",
                  }}
                >
                  <Text size="xs" fw={700} style={{ color: "rgba(17, 24, 39, 0.98)" }}>
                    {notifications.length}
                  </Text>
                </Box>
              )}
            </Box>

            {/* User Profile Menu */}
            <Menu 
              shadow="lg" 
              width={280} 
              position="bottom-end"
              transitionProps={{ transition: 'pop-top-right' }}
              withinPortal
            >
              <Menu.Target>
                <UnstyledButton
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    height: 46,
                    padding: "0 18px 0 12px",
                    borderRadius: "18px",
                    background: "rgba(255, 255, 255, 0.06)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.22)",
                    backdropFilter: "blur(8px)",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.10)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <Box
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      background: getRoleGradient(userRole),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)",
                      border: "1px solid rgba(255,255,255,0.15)",
                    }}
                  >
                    <Text size="xs" fw={800} style={{ color: "white", letterSpacing: "0.02em" }}>
                      {userInitials}
                    </Text>
                  </Box>
                  <Box>
                    <Text size="sm" fw={700} style={{ color: "rgba(255, 255, 255, 0.98)", letterSpacing: "0.01em", maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                       {userName}
                     </Text>
                     <Group gap={4}>
                       <IconCircleDot size={8} style={{ color: "#10b981" }} />
                       <Text size="xs" style={{ color: "rgba(255, 255, 255, 0.65)" }}>
                         {userRole}
                       </Text>
                     </Group>
                  </Box>
                  <IconChevronRight size={14} style={{ color: "rgba(255, 255, 255, 0.45)" }} />
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown
                style={{
                  background: "rgba(31, 41, 55, 0.98)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                }}
              >
                <Box p="sm">
                  <Group>
                    <Box
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        background: getRoleGradient(userRole),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      <Text size="md" fw={700} style={{ color: "white" }}>
                        {userInitials}
                      </Text>
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Text size="sm" fw={600} style={{ color: "rgba(255, 255, 255, 0.95)" }}>
                        {userName}
                      </Text>
                      <Text size="xs" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                        {userEmail}
                      </Text>
                      {staffProfile?.department && (
                        <Badge
                          size="xs"
                          style={{
                            background: "rgba(102, 126, 234, 0.2)",
                            color: "#a5b4fc",
                            border: "1px solid rgba(102, 126, 234, 0.3)",
                            marginTop: 4,
                          }}
                        >
                          {staffProfile.department}
                        </Badge>
                      )}
                    </Box>
                  </Group>
                </Box>

                <Divider color="rgba(255, 255, 255, 0.08)" />

                <Menu.Item 
                  leftSection={<IconUser size={16} />}
                  onClick={() => navigate('/staff/profile')}
                  style={{ color: "rgba(255, 255, 255, 0.8)" }}
                >
                  View Profile
                </Menu.Item>
                
                <Menu.Item 
                  leftSection={<IconSettings size={16} />}
                  onClick={() => navigate('/settings')}
                  style={{ color: "rgba(255, 255, 255, 0.8)" }}
                >
                  Settings
                </Menu.Item>

                <Divider color="rgba(255, 255, 255, 0.08)" />

                <Menu.Item 
                  leftSection={<IconLogout size={16} />}
                  onClick={async () => {
                    const result = await logout();
                    if (result?.success) {
                      window.location.href = "/auth";
                    }
                  }}
                  style={{ color: "#f87171" }}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
        
        {/* Offline Banner */}
        {!isOnline && (
          <Box
            style={{
              background: "linear-gradient(90deg, var(--mantine-color-accent-6), var(--mantine-color-amber-6))",
              padding: "8px",
              textAlign: "center",
            }}
          >
            <Text size="xs" fw={600} style={{ color: "white" }}>
              You are currently offline. Some features may be unavailable.
            </Text>
          </Box>
        )}
      </AppShell.Header>

      {/* Notification Drawer */}
      <Drawer 
        opened={isNotificationDrawerOpen} 
        onClose={() => setIsNotificationDrawerOpen(false)} 
        title="Notifications" 
        position="right"
        styles={{
          drawer: {
            background: "rgba(31, 41, 55, 0.98)",
            backdropFilter: "blur(20px)",
          },
          header: {
            background: "rgba(17, 24, 39, 0.98)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          },
          title: {
            color: "rgba(255, 255, 255, 0.95)",
            fontWeight: 600,
          },
        }}
      >
        <NotificationDrawer onClose={() => setIsNotificationDrawerOpen(false)} />
      </Drawer>

    </>
  );
};

export default Header;